import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_GLASS_CARD, SITE_FORM_CONTROL } from '../constants/siteUi';
import { fetchPublishedResourceArticles } from '../lib/investorResourceApi';
import {
  RESOURCE_ARTICLES,
  RESOURCE_CATEGORY_LABEL,
  type ResourceArticle,
  type ResourceCategory,
  type ResourceDifficulty,
} from '../data/investorResourceArticles';

const ALL_KEY = 'all' as const;

type FilterKey = typeof ALL_KEY | ResourceCategory;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: ALL_KEY, label: 'All articles' },
  { key: 'financing', label: RESOURCE_CATEGORY_LABEL.financing },
  { key: 'legal', label: 'Legal considerations' },
  { key: 'market-research', label: RESOURCE_CATEGORY_LABEL['market-research'] },
  { key: 'property-analysis', label: RESOURCE_CATEGORY_LABEL['property-analysis'] },
  { key: 'property-management', label: RESOURCE_CATEGORY_LABEL['property-management'] },
];

function difficultyClass(d: ResourceDifficulty): string {
  switch (d) {
    case 'beginner':
      return 'bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30';
    case 'intermediate':
      return 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/30';
    case 'advanced':
      return 'bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/30';
    default:
      return 'bg-white/10 text-[var(--home-muted-on-dark)]';
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function InvestorResources() {
  const [filter, setFilter] = useState<FilterKey>(ALL_KEY);
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState<ResourceArticle[]>(RESOURCE_ARTICLES);

  useEffect(() => {
    void fetchPublishedResourceArticles().then((fromApi) => {
      if (fromApi !== null) setArticles(fromApi);
    });
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles
      .filter((a) => {
        if (filter !== ALL_KEY && a.category !== filter) return false;
        if (!q) return true;
        const blob = `${a.title} ${a.description} ${a.tags.join(' ')}`.toLowerCase();
        return blob.includes(q);
      })
      .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }, [filter, query, articles]);

  return (
    <div className="space-y-10">
      <header className="space-y-6">
        <div className="space-y-2 text-center lg:text-left">
          <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-[var(--home-miami-blue)]">
            Investor portal
          </p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
            Knowledge base
          </h1>
          <p className="mx-auto max-w-2xl font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)] lg:mx-0">
            Learn the essentials of multifamily investing—from financing and underwriting to operations and compliance.
          </p>
        </div>

        <div className="relative mx-auto max-w-xl lg:mx-0">
          <input
            type="search"
            placeholder="Search articles and topics…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={`w-full pr-10 ${SITE_FORM_CONTROL}`}
            aria-label="Search resources"
          />
          <span
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--home-muted-on-dark)]"
            aria-hidden
          >
            <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map(({ key, label }) => {
          const active = filter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-full px-4 py-2 font-body text-xs font-medium transition ${
                active
                  ? 'bg-[var(--home-miami-blue)] text-white shadow-lg shadow-black/20'
                  : 'border border-white/12 bg-white/[0.04] text-[var(--home-text-on-dark)] hover:bg-white/[0.08]'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <p className="font-body text-sm text-[var(--home-muted-on-dark)]">
        Showing <span className="font-semibold text-[var(--home-text-on-dark)]">{filtered.length}</span> article
        {filtered.length === 1 ? '' : 's'}
        {query.trim() ? ` for “${query.trim()}”` : ''}
      </p>

      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((a) => (
          <li key={a.slug}>
            <Link
              to={`/dashboard/resources/${a.slug}`}
              className={`${SITE_GLASS_CARD} flex h-full flex-col p-5 transition hover:border-[var(--home-miami-blue)]/35 hover:bg-white/[0.03]`}
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="rounded-full bg-[var(--home-miami-blue)]/20 px-2.5 py-0.5 font-body text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--home-miami-blue)]">
                  {RESOURCE_CATEGORY_LABEL[a.category]}
                </span>
                <span
                  className={`rounded-full px-2.5 py-0.5 font-body text-[0.65rem] font-semibold capitalize ${difficultyClass(a.difficulty)}`}
                >
                  {a.difficulty}
                </span>
              </div>
              <h2 className="font-display text-base font-bold leading-snug text-[var(--home-text-on-dark)]">{a.title}</h2>
              <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)]">
                {a.description}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 font-body text-xs text-[var(--home-muted-on-dark)]">
                <span className="inline-flex items-center gap-1">
                  <svg className="size-3.5 shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {a.readMinutes} min read
                </span>
                <span className="inline-flex items-center gap-1">
                  <svg className="size-3.5 shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(a.publishedAt)}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {a.tags.slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-0.5 font-mono text-[0.6rem] text-[var(--home-text-on-dark)]/80"
                  >
                    {t}
                  </span>
                ))}
                {a.tags.length > 3 ? (
                  <span className="self-center font-body text-[0.65rem] text-[var(--home-muted-on-dark)]">
                    +{a.tags.length - 3} more
                  </span>
                ) : null}
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {filtered.length === 0 ? (
        <p className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-8 text-center font-body text-sm text-[var(--home-muted-on-dark)]">
          No articles match your filters. Try another category or search term.
        </p>
      ) : null}
    </div>
  );
}
